/**
 * Subscribe & Save — Phase C interactivity (Team Logic).
 * Vanilla ES module, no imports. All behavior mirrors the prototype's DCLogic
 * math (prices/r10/fmt/visibleList/addAll/renderVals) against the DOM contract
 * in sections/subscribe-and-save.liquid.
 */
(() => {
  'use strict';

  const SS_KEY = 'sns:cart:v1';
  const ALL = 'All Products';

  function init() {
    const root = document.querySelector('[data-sns]');
    if (!root) return;

    /* ---------------- payload ---------------- */
    let payload = null;
    const payloadEl = root.querySelector('script[data-sns-products]');
    try {
      payload = JSON.parse(payloadEl ? payloadEl.textContent : 'null');
    } catch (e) {
      payload = null;
    }
    if (!payload || !Array.isArray(payload.products) || payload.products.length === 0) {
      console.warn('[sns] Product payload missing or empty — Subscribe & Save left inert.');
      return;
    }
    const products = payload.products.filter((p) => p && p.id != null);
    if (!products.length) {
      console.warn('[sns] No usable products in payload — Subscribe & Save left inert.');
      return;
    }
    /* Payload strings arrive HTML-escaped (e.g. "&amp;") and we render via
       textContent — decode entities once per product, up front. */
    const decodeTa = document.createElement('textarea');
    const decode = (s) => {
      if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
      decodeTa.innerHTML = s;
      return decodeTa.value;
    };
    products.forEach((p) => {
      p.title = decode(p.title);
      p.desc = decode(p.desc);
      p.pack = decode(p.pack);
      p.ingredients = decode(p.ingredients);
      p.diet = decode(p.diet);
      p.meantFor = decode(p.meantFor);
      p.shelfLife = decode(p.shelfLife);
      if (Array.isArray(p.goals)) p.goals = p.goals.map(decode);
      if (Array.isArray(p.highlights)) p.highlights = p.highlights.map(decode);
      /* supplementFacts is NOT decoded here — it may be JSON and must reach
         JSON.parse untouched; its strings are decoded after parsing. */
    });
    if (Array.isArray(payload.goalOrder)) payload.goalOrder = payload.goalOrder.map(decode);
    if (Array.isArray(payload.placeholders)) payload.placeholders = payload.placeholders.map(decode);
    const byId = new Map(products.map((p) => [String(p.id), p]));

    /* ---------------- helpers ---------------- */
    const qs = (el, sel) => (el ? el.querySelector(sel) : null);
    const qsa = (el, sel) => (el ? Array.from(el.querySelectorAll(sel)) : []);
    const setText = (scope, sel, text) => {
      const el = qs(scope, sel);
      if (el) el.textContent = text;
    };
    const debounce = (fn, ms) => {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), ms);
      };
    };

    /* Money (prototype fmt/r10) */
    const fmt = (n) => '₹' + Math.round(n).toLocaleString('en-IN');
    const r10 = (n) => Math.round(n / 10) * 10;
    /* discountPct/discountBasis come from the section's schema settings
       (Subscribe & Save > Subscription discount / Apply discount to). The
       subscription price is always base * (1 - pct/100); which field is the
       base depends on the merchant's chosen basis:
       - compare_at: base is compareAt (or a +10% estimate when a product has
         none) — the strike-through stays put, the subscription price moves.
       - selling_price: base is the variant's real price — that price becomes
         the new strike-through, and the subscription price is discounted
         further off it. */
    const discountPct = Math.max(0, Math.min(100, Number(payload.discountPct) || 0));
    const discountBasis = payload.discountBasis === 'selling_price' ? 'selling_price' : 'compare_at';
    const pricesOf = (p) => {
      const price = Number(p.price) || 0;
      const compareAt = Number(p.compareAt);
      const base = discountBasis === 'selling_price' ? price : compareAt > 0 ? compareAt : r10(price * 1.1);
      const mrp = base;
      const sub = base * (1 - discountPct / 100);
      return { mrp, sub, save: mrp - sub };
    };
    const countLabel = (n) => n + (n === 1 ? ' item' : ' items');
    const ratingCountLabel = (rc) =>
      rc == null || rc === '' ? '' : '(' + Number(rc).toLocaleString('en-IN') + ')';

    /* ---------------- element refs ---------------- */
    const els = {
      search: qs(root, '[data-sns-search]'),
      searchClear: qs(root, '[data-sns-search-clear]'),
      track: qs(root, '[data-sns-tabs-track]'),
      fadeL: qs(root, '[data-sns-tabs-fade="left"]'),
      fadeR: qs(root, '[data-sns-tabs-fade="right"]'),
      tabsPrev: qs(root, '[data-sns-tabs-prev]'),
      tabsNext: qs(root, '[data-sns-tabs-next]'),
      rows: qs(root, '[data-sns-rows]'),
      listHeader: qs(root, '.sns-list__header'),
      addall: qs(root, '[data-sns-addall]'),
      addallLabel: qs(root, '[data-sns-addall-label]'),
      empty: qs(root, '[data-sns-empty]'),
      stepCells: qsa(root, '[data-sns-step]'),
      cart: qs(root, '[data-sns-cart]'),
      cartCount: qs(root, '[data-sns-cart-count]'),
      cartItems: qs(root, '[data-sns-cart-items]'),
      cartEmpty: qs(root, '[data-sns-cart-empty]'),
      cartTotals: qs(root, '[data-sns-cart-totals]'),
      bottombar: qs(root, '[data-sns-bottombar]'),
      thumbs: qs(root, '[data-sns-thumbs]'),
      bbCount: qs(root, '[data-sns-count]'),
      cartSheet: qs(root, '[data-sns-sheet="cart"]'),
      sheetItems: qs(root, '[data-sns-sheet-items]'),
      sheetEmpty: qs(root, '[data-sns-sheet-empty]'),
      sheetTotals: qs(root, '[data-sns-sheet-totals]'),
      qvDesktop: qs(root, '[data-sns-quickview="desktop"]'),
      qvMobile: qs(root, '[data-sns-sheet="product"]'),
      tplRow: qs(root, 'template[data-sns-template="row"]'),
      tplCartItem: qs(root, 'template[data-sns-template="cart-item"]'),
      tplSheetItem: qs(root, 'template[data-sns-template="sheet-item"]')
    };

    /* ---------------- state ---------------- */
    const state = {
      qty: {}, // { productId(string): n }
      filter: ALL,
      query: '',
      openSheet: null, // 'cart' | null
      openProduct: null // product id (string) | null
    };

    function persist() {
      try {
        sessionStorage.setItem(SS_KEY, JSON.stringify(state.qty));
      } catch (e) {
        /* storage unavailable — non-fatal */
      }
    }
    (function restore() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(SS_KEY) || 'null');
        if (!saved || typeof saved !== 'object') return;
        Object.keys(saved).forEach((id) => {
          const p = byId.get(id);
          const n = Math.floor(Number(saved[id]));
          if (p && p.available && n > 0) state.qty[id] = n;
        });
      } catch (e) {
        /* corrupt storage — start fresh */
      }
    })();

    const qtyOf = (id) => state.qty[id] || 0;
    function mutateQty(id, next) {
      if (next <= 0) delete state.qty[id];
      else state.qty[id] = next;
      persist();
      render();
    }
    const stepQty = (id, delta) => mutateQty(id, Math.max(0, qtyOf(id) + delta));
    const removeItem = (id) => mutateQty(id, 0);

    /* ---------------- goals & filtering ---------------- */
    const goalList = (() => {
      const seen = [];
      products.forEach((p) =>
        (p.goals || []).forEach((g) => {
          if (g && seen.indexOf(g) === -1) seen.push(g);
        })
      );
      const ordered = [];
      (payload.goalOrder || []).forEach((g) => {
        if (seen.indexOf(g) !== -1 && ordered.indexOf(g) === -1) ordered.push(g);
      });
      seen.forEach((g) => {
        if (ordered.indexOf(g) === -1) ordered.push(g);
      });
      return ordered;
    })();
    const goalCount = (g) => products.filter((p) => (p.goals || []).indexOf(g) !== -1).length;

    function visibleList() {
      const q = state.query.trim().toLowerCase();
      return products.filter((p) => {
        const goals = p.goals || [];
        const goalOk = state.filter === ALL || goals.indexOf(state.filter) !== -1;
        if (!goalOk) return false;
        if (!q) return true;
        const hay = ((p.title || '') + ' ' + (p.desc || '') + ' ' + goals.join(' ')).toLowerCase();
        return hay.indexOf(q) !== -1;
      });
    }

    /* ---------------- tabs (built once; only is-active toggles after) ------- */
    function buildTabs() {
      const track = els.track;
      if (!track) return;
      const allTab = qs(track, '[data-sns-tab]');
      if (allTab && !qs(allTab, '.sns-tabs__count')) {
        const c = document.createElement('span');
        c.className = 'sns-tabs__count';
        c.textContent = '(' + products.length + ')';
        allTab.appendChild(document.createTextNode(' '));
        allTab.appendChild(c);
      }
      if (payload.showGoalTabs === false) return; // "All Products" tab (built above) stays; skip goal + "Soon" tabs.
      goalList.forEach((g) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sns-tabs__tab';
        btn.setAttribute('data-sns-tab', '');
        btn.setAttribute('data-goal', g);
        btn.setAttribute('aria-pressed', 'false');
        btn.textContent = g + ' ';
        const c = document.createElement('span');
        c.className = 'sns-tabs__count';
        c.textContent = '(' + goalCount(g) + ')';
        btn.appendChild(c);
        track.appendChild(btn);
      });
      (payload.placeholders || []).forEach((label) => {
        if (!label) return;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sns-tabs__tab is-soon';
        btn.disabled = true;
        btn.title = 'Coming soon';
        btn.textContent = label + ' ';
        const s = document.createElement('span');
        s.className = 'sns-tabs__soon';
        s.textContent = 'Soon';
        btn.appendChild(s);
        track.appendChild(btn);
      });
    }
    function renderTabsActive() {
      qsa(els.track, '[data-sns-tab]').forEach((tab) => {
        const active = tab.getAttribute('data-goal') === state.filter;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-pressed', String(active));
      });
    }

    /* ---------------- tabs scroller ---------------- */
    const scrollTabs = (dir) => {
      if (els.track) els.track.scrollBy({ left: dir * 320, behavior: 'smooth' });
    };
    function updateTabScroll() {
      const el = els.track;
      if (!el) return;
      const atStart = el.scrollLeft <= 2;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
      if (els.tabsPrev) els.tabsPrev.hidden = atStart;
      if (els.fadeL) els.fadeL.hidden = atStart;
      if (els.tabsNext) els.tabsNext.hidden = atEnd;
      if (els.fadeR) els.fadeR.hidden = atEnd;
    }

    /* ---------------- steps banner ---------------- */
    function stepsFor(filter) {
      const goals = (payload.steps && payload.steps.goals) || [];
      const hit = goals.find((g) => g && g.label === filter && Array.isArray(g.items) && g.items.length);
      if (hit) return hit.items;
      return (payload.steps && payload.steps.default && payload.steps.default.items) || null;
    }
    function stepsCountFor(filter) {
      return filter === ALL ? products.length : goalCount(filter);
    }
    function withStepCount(text, count) {
      return text == null ? text : String(text).replace(/\{count\}/g, count);
    }
    function renderSteps() {
      const items = stepsFor(state.filter);
      if (!items) return;
      const count = stepsCountFor(state.filter);
      els.stepCells.forEach((cell, i) => {
        const it = items[i];
        if (!it) return;
        if (it.title != null) setText(cell, '[data-sns-step-title]', withStepCount(it.title, count));
        if (it.sub != null) setText(cell, '[data-sns-step-sub]', withStepCount(it.sub, count));
      });
    }

    /* ---------------- product rows ---------------- */
    function fillImage(scope, p) {
      const img = qs(scope, '[data-sns-image]');
      if (!img) return;
      if (p.image) {
        img.src = p.image;
        img.alt = p.title || '';
      } else {
        img.hidden = true; // leave the container's placeholder background
      }
    }
    function fillRating(scope, wrapSel, ratingSel, countSel, p) {
      const wrap = qs(scope, wrapSel);
      if (!wrap) return;
      const r = parseFloat(p.rating);
      if (isNaN(r)) {
        wrap.hidden = true;
        return;
      }
      wrap.hidden = false;
      setText(scope, ratingSel, r.toFixed(1));
      const rc = qs(scope, countSel);
      if (rc) rc.textContent = ratingCountLabel(p.ratingCount);
    }

    function buildRow(p) {
      const node = els.tplRow.content.firstElementChild.cloneNode(true);
      const id = String(p.id);
      node.dataset.productId = id;
      const pr = pricesOf(p);

      fillImage(node, p);
      setText(node, '[data-sns-pack]', p.pack || '');
      setText(node, '[data-sns-pack-desktop]', p.pack || '');
      setText(node, '[data-sns-name]', p.title || '');
      setText(node, '[data-sns-desc]', p.desc || '');
      fillRating(node, '[data-sns-rating-wrap]', '[data-sns-rating]', '[data-sns-rating-count]', p);

      const goal = payload.showGoalPill !== false && (p.goals || [])[0];
      const goalEl = qs(node, '[data-sns-goal]');
      if (goalEl) {
        if (goal) goalEl.textContent = goal;
        else goalEl.hidden = true;
      }
      const stock = qs(node, '[data-sns-stock]');
      if (stock) stock.classList.add(p.available ? 'is-in' : 'is-out');

      setText(node, '[data-sns-price-sub]', fmt(pr.sub));
      setText(node, '[data-sns-price-strike]', fmt(pr.mrp));
      setText(node, '[data-sns-save-label]', 'You save ' + fmt(pr.save) + ' / delivery');

      const q = qtyOf(id);
      const add = qs(node, '[data-sns-add]');
      const stepper = qs(node, '[data-sns-stepper]');
      const sold = qs(node, '[data-sns-soldout]');
      if (!p.available) {
        node.classList.add('is-soldout');
        if (add) add.hidden = true;
        if (stepper) stepper.hidden = true;
        if (sold) sold.hidden = false;
      } else if (q > 0) {
        if (add) add.hidden = true;
        if (stepper) {
          stepper.hidden = false;
          setText(stepper, '[data-sns-qty]', String(q));
        }
      }
      return node;
    }

    /* Track which row control has focus so re-renders don't strand keyboard users. */
    function captureRowFocus() {
      const act = document.activeElement;
      if (!act || !els.rows || !els.rows.contains(act)) return null;
      const row = act.closest('[data-product-id]');
      if (!row) return null;
      let hook = null;
      if (act.hasAttribute('data-sns-inc')) hook = '[data-sns-inc]';
      else if (act.hasAttribute('data-sns-dec')) hook = '[data-sns-dec]';
      else if (act.hasAttribute('data-sns-add')) hook = '[data-sns-add]';
      return hook ? { id: row.dataset.productId, hook } : null;
    }
    function restoreRowFocus(memo) {
      if (!memo) return;
      const row = qsa(els.rows, '[data-product-id]').find((r) => r.dataset.productId === memo.id);
      if (!row) return;
      const stepperEl = qs(row, '[data-sns-stepper]');
      const stepperVisible = !!stepperEl && !stepperEl.hidden;
      let el = qs(row, memo.hook);
      if (el && (el.hidden || (el.closest('[data-sns-stepper]') && !stepperVisible))) el = null;
      if (!el) el = stepperVisible ? qs(row, '[data-sns-inc]') : qs(row, '[data-sns-add]');
      if (el && !el.hidden) el.focus();
    }

    function renderRows() {
      if (!els.tplRow || !els.rows) return;
      const memo = captureRowFocus();
      const visible = visibleList();
      els.rows.textContent = '';
      const frag = document.createDocumentFragment();
      visible.forEach((p) => frag.appendChild(buildRow(p)));
      els.rows.appendChild(frag);
      restoreRowFocus(memo);

      const none = visible.length === 0;
      if (els.empty) els.empty.hidden = !none;
      if (els.listHeader) els.listHeader.hidden = none;
      renderAddAll(visible, none);
    }

    function renderAddAll(visible, none) {
      if (!els.addall) return;
      const addable = visible.filter((p) => p.available && qtyOf(String(p.id)) === 0);
      const show = !none && addable.length > 0;
      els.addall.hidden = !show;
      if (show && els.addallLabel) {
        els.addallLabel.textContent =
          state.filter === ALL ? 'Add all ' + addable.length + ' products' : 'Add all ' + state.filter;
      }
    }

    function addAll() {
      visibleList().forEach((p) => {
        const id = String(p.id);
        if (p.available && qtyOf(id) === 0) state.qty[id] = 1;
      });
      persist();
      render();
    }

    /* ---------------- cart math & rendering ---------------- */
    function cartData() {
      const items = [];
      let count = 0;
      let total = 0;
      let subMrp = 0;
      products.forEach((p) => {
        const q = qtyOf(String(p.id));
        if (q <= 0) return;
        const pr = pricesOf(p);
        const line = pr.sub * q;
        const mrpLine = pr.mrp * q;
        count += q;
        total += line;
        subMrp += mrpLine;
        items.push({ p, q, pr, line, mrpLine, savedLine: (pr.mrp - pr.sub) * q });
      });
      return { items, count, total, subMrp, savings: subMrp - total };
    }

    /* Totals hooks are duplicated across containers — always fill scoped. */
    function fillTotals(scope, data) {
      if (!scope) return;
      const t = qs(scope, '[data-sns-total]');
      if (t) t.textContent = fmt(data.total);
      const s = qs(scope, '[data-sns-savings]');
      if (s) s.textContent = '− ' + fmt(data.savings);
      const bc = qs(scope, '[data-sns-breakdown-count]');
      if (bc) bc.textContent = 'Subtotal (' + countLabel(data.items.length) + ' · MRP)';
      const sm = qs(scope, '[data-sns-subtotal-mrp]');
      if (sm) sm.textContent = fmt(data.subMrp);
    }

    function buildCartItem(it) {
      const node = els.tplCartItem.content.firstElementChild.cloneNode(true);
      node.dataset.productId = String(it.p.id);
      fillImage(node, it.p);
      setText(node, '[data-sns-name]', it.p.title || '');
      setText(node, '[data-sns-price]', fmt(it.line));
      setText(node, '[data-sns-strike]', fmt(it.mrpLine));
      setText(node, '[data-sns-qty]', String(it.q));
      return node;
    }
    function buildSheetItem(it) {
      const node = els.tplSheetItem.content.firstElementChild.cloneNode(true);
      node.dataset.productId = String(it.p.id);
      fillImage(node, it.p);
      setText(node, '[data-sns-name]', it.p.title || '');
      setText(node, '[data-sns-pack]', it.p.pack || '');
      setText(node, '[data-sns-qty]', String(it.q));
      setText(node, '[data-sns-price]', fmt(it.line));
      setText(node, '[data-sns-strike]', fmt(it.mrpLine));
      setText(node, '[data-sns-saved]', fmt(it.savedLine) + ' saved');
      return node;
    }

    /* Replace only the first text node (the bottombar count sits next to an svg). */
    function setLeadingText(el, text) {
      if (!el) return;
      for (const n of el.childNodes) {
        if (n.nodeType === Node.TEXT_NODE) {
          n.textContent = text + ' ';
          return;
        }
      }
      el.insertBefore(document.createTextNode(text + ' '), el.firstChild);
    }

    function renderCart() {
      const data = cartData();
      const empty = data.count === 0;

      /* Desktop aside */
      if (els.cartCount) els.cartCount.textContent = countLabel(data.items.length);
      if (els.cartItems && els.tplCartItem) {
        qsa(els.cartItems, '[data-sns-cart-item]').forEach((n) => n.remove());
        const frag = document.createDocumentFragment();
        data.items.forEach((it) => frag.appendChild(buildCartItem(it)));
        els.cartItems.appendChild(frag);
      }
      if (els.cartEmpty) els.cartEmpty.hidden = !empty;
      if (els.cartTotals) {
        els.cartTotals.hidden = empty;
        fillTotals(els.cartTotals, data);
      }

      /* Mobile cart sheet */
      if (els.sheetItems && els.tplSheetItem) {
        qsa(els.sheetItems, '[data-sns-sheet-item]').forEach((n) => n.remove());
        const frag = document.createDocumentFragment();
        data.items.forEach((it) => frag.appendChild(buildSheetItem(it)));
        els.sheetItems.appendChild(frag);
      }
      if (els.sheetEmpty) els.sheetEmpty.hidden = !empty;
      if (els.sheetTotals) {
        els.sheetTotals.hidden = empty;
        fillTotals(els.sheetTotals, data);
      }

      /* Bottom bar (mobile-only via CSS; visible only when the cart has items) */
      if (els.bottombar) {
        els.bottombar.hidden = empty;
        if (!empty) {
          const t = qs(els.bottombar, '[data-sns-total]');
          if (t) t.textContent = fmt(data.total);
          const sv = qs(els.bottombar, '[data-sns-saved]');
          if (sv) sv.textContent = fmt(data.savings) + ' saved';
          setLeadingText(els.bbCount, countLabel(data.items.length));
          if (els.thumbs) {
            els.thumbs.textContent = '';
            data.items.slice(0, 2).forEach((it) => {
              const s = document.createElement('span');
              s.className = 'sns-bottombar__thumb';
              if (it.p.image) s.style.backgroundImage = 'url("' + it.p.image + '")';
              els.thumbs.appendChild(s);
            });
          }
        }
      }
      updateBottombarSpacer();
    }

    /* Mobile-only: keep --sns-bottombar-h in sync with the bar's real rendered
       height (0 when hidden/cart empty) so the cart sheet (via CSS) can sit
       above it instead of covering it. Also keeps --sns-themenav-h in sync
       with the theme's own persistent mobile nav bar (.mobile_stickey_menu,
       rendered by sections/header.liquid on every page) so our bar stacks
       above it instead of covering it — same call sites cover both since
       neither height changes except on resize/rotation. */
    function updateBottombarSpacer() {
      const h = els.bottombar && !els.bottombar.hidden ? els.bottombar.getBoundingClientRect().height : 0;
      root.style.setProperty('--sns-bottombar-h', Math.round(h) + 'px');
      const themeNav = document.querySelector('.mobile_stickey_menu');
      const navH = themeNav ? themeNav.getBoundingClientRect().height : 0;
      root.style.setProperty('--sns-themenav-h', Math.round(navH) + 'px');
    }

    /* ---------------- quick-view ---------------- */
    function labelOf(el) {
      const prev = el && el.previousElementSibling;
      if (prev && (prev.classList.contains('sns-modal__label') || prev.classList.contains('sns-qv-label')))
        return prev;
      return null;
    }
    /* Show text in el, hiding it and its section label when the value is empty. */
    function setBlock(el, value) {
      if (!el) return;
      const label = labelOf(el);
      const on = value != null && value !== '';
      el.hidden = !on;
      if (label) label.hidden = !on;
      if (on) el.textContent = value;
    }
    const stripHtml = (s) => String(s).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

    function fillQvControls(shell, p) {
      if (!shell) return;
      const q = qtyOf(String(p.id));
      const add = qs(shell, '[data-sns-qv-add]');
      const stepper = qs(shell, '[data-sns-qv-stepper]');
      const sold = qs(shell, '[data-sns-qv-soldout]');
      if (!p.available) {
        if (add) add.hidden = true;
        if (stepper) stepper.hidden = true;
        if (sold) sold.hidden = false;
      } else if (q > 0) {
        if (add) add.hidden = true;
        if (sold) sold.hidden = true;
        if (stepper) {
          stepper.hidden = false;
          setText(stepper, '[data-sns-qv-qty]', String(q));
        }
      } else {
        if (add) add.hidden = false;
        if (stepper) stepper.hidden = true;
        if (sold) sold.hidden = true;
      }
    }

    function fillQvShared(shell, p, pr) {
      if (!shell) return;
      fillRating(shell, '[data-sns-qv-rating-wrap]', '[data-sns-qv-rating]', '[data-sns-qv-rating-count]', p);
      setText(shell, '[data-sns-qv-name]', p.title || '');
      const goal = payload.showGoalPill !== false ? (p.goals || [])[0] || '' : '';
      const goalEl = qs(shell, '[data-sns-qv-goal]');
      if (goalEl) {
        goalEl.textContent = goal;
        goalEl.hidden = !goal;
      }
      setText(shell, '[data-sns-qv-price]', fmt(pr.sub));
      setText(shell, '[data-sns-qv-strike]', fmt(pr.mrp));
      setText(shell, '[data-sns-qv-save]', 'Save ' + fmt(pr.save) + ' / delivery');

      const ul = qs(shell, '[data-sns-qv-highlights]');
      if (ul) {
        ul.textContent = '';
        const hs = Array.isArray(p.highlights) ? p.highlights.filter(Boolean) : [];
        hs.forEach((h) => {
          const li = document.createElement('li');
          li.textContent = h;
          ul.appendChild(li);
        });
        const lbl = labelOf(ul);
        ul.hidden = hs.length === 0;
        if (lbl) lbl.hidden = hs.length === 0;
      }

      const veg = qs(shell, '[data-sns-qv-vegetarian]');
      if (veg) veg.hidden = !/vegetarian/i.test(p.diet || '');

      /* Prototype longDesc fallback. */
      const descEl = qs(shell, '[data-sns-qv-desc]');
      if (descEl) {
        const d = p.desc
          ? p.desc +
            ' Taken daily as part of your subscription, it is delivered fresh to your door every 30 days so you never miss a dose.'
          : null;
        setBlock(descEl, d);
      }

      setText(shell, '[data-sns-qv-footer-price]', fmt(pr.sub));
      setText(shell, '[data-sns-qv-footer-strike]', fmt(pr.mrp));
      setText(shell, '[data-sns-qv-footer-pack]', p.pack || '');
      fillQvControls(shell, p);
    }

    function fillQvDesktop(p, pr) {
      const shell = els.qvDesktop;
      if (!shell) return;
      fillQvShared(shell, p, pr);
      const img = qs(shell, '[data-sns-qv-image]');
      if (img) {
        img.hidden = !p.image;
        if (p.image) {
          img.src = p.image;
          img.alt = p.title || '';
        }
      }
      setText(shell, '[data-sns-qv-pack]', p.pack || '');
      const sep = qs(shell, '[data-sns-qv-sep]');
      if (sep) sep.hidden = !(payload.showGoalPill !== false && (p.goals || [])[0]);
      fillQvIngredients(shell, p);
    }

    /* Supplement facts arrive as a JSON string OR an already-parsed object
       (JSON-type metafield .value), in one of three shapes:
       1. bare rows array:            [{name, sub?, amount, rda}]
       2. documented wrapper:         {servingPerContainer, servingSize, rows, note}
       3. store metafield shape:      {serving_per_container, serving_size,
                                       facts_title, ingredients: [{name, amount,
                                       rda}], other_ingredients: [str], uthever_note}
       parseFacts normalizes all three; returns null when nothing parseable. */
    function parseFacts(raw) {
      if (raw == null || raw === '') return null;
      let data = raw;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          return null;
        }
      }
      if (!data || typeof data !== 'object') return null;
      const meta = Array.isArray(data) ? {} : data;
      const serving = meta.serving && typeof meta.serving === 'object' ? meta.serving : meta;
      const pick = (...vals) => {
        for (const v of vals) if (v != null && v !== '') return v;
        return null;
      };
      let rows = Array.isArray(data)
        ? data
        : Array.isArray(meta.rows)
          ? meta.rows
          : Array.isArray(meta.ingredients)
            ? meta.ingredients
            : null;
      if (!rows || !rows.length || !rows.every((r) => r && r.name)) rows = null;
      return {
        rows,
        servingPerContainer: pick(serving.servingPerContainer, serving.serving_per_container, meta.serving_per_container),
        servingSize: pick(serving.servingSize, serving.serving_size, meta.serving_size),
        note: pick(meta.note, meta.uthever_note),
        title: pick(meta.facts_title) || 'Supplements Fact',
        otherTitle: pick(meta.ingredients_title) || 'Other Ingredients',
        other: Array.isArray(meta.other_ingredients)
          ? meta.other_ingredients.filter((s) => typeof s === 'string' && s.trim()).map((s) => decode(s))
          : []
      };
    }

    /* Render the .sns-facts panel from parseFacts output (skipped without rows). */
    function buildFacts(container, parsed) {
      if (!container) return false;
      container.textContent = '';
      container.hidden = true;
      if (!parsed || !parsed.rows) return false;
      const rows = parsed.rows;

      const panel = document.createElement('div');
      panel.className = 'sns-facts';

      const title = document.createElement('div');
      title.className = 'sns-facts__title';
      title.textContent = decode(String(parsed.title));
      panel.appendChild(title);

      if (parsed.servingPerContainer != null || parsed.servingSize != null) {
        const sv = document.createElement('div');
        sv.className = 'sns-facts__serving';
        if (parsed.servingPerContainer != null) {
          const a = document.createElement('span');
          a.textContent = 'Serving per container: ' + decode(String(parsed.servingPerContainer));
          sv.appendChild(a);
        }
        if (parsed.servingSize != null) {
          const b = document.createElement('span');
          b.textContent = 'Serving Size: ' + decode(String(parsed.servingSize));
          sv.appendChild(b);
        }
        panel.appendChild(sv);
      }

      const bar = document.createElement('div');
      bar.className = 'sns-facts__bar';
      panel.appendChild(bar);

      const head = document.createElement('div');
      head.className = 'sns-facts__head';
      [
        ['sns-facts__col-name', 'Ingredients'],
        ['sns-facts__col-amount', 'Amount per Serving'],
        ['sns-facts__col-rda', '% RDA']
      ].forEach(([cls, txt]) => {
        const c = document.createElement('div');
        c.className = cls;
        c.textContent = txt;
        head.appendChild(c);
      });
      panel.appendChild(head);

      rows.forEach((r) => {
        const row = document.createElement('div');
        row.className = 'sns-facts__row';
        const name = document.createElement('div');
        name.className = 'sns-facts__name';
        name.textContent = decode(r.name);
        if (r.sub) {
          const sub = document.createElement('span');
          sub.className = 'sns-facts__sub';
          const subText = decode(String(r.sub)).trim();
          sub.textContent = subText.charAt(0) === '(' ? ' ' + subText : ' (' + subText + ')';
          name.appendChild(sub);
        }
        const amount = document.createElement('div');
        amount.className = 'sns-facts__amount';
        amount.textContent = r.amount != null ? decode(String(r.amount)) : '';
        const rda = document.createElement('div');
        rda.className = 'sns-facts__rda';
        rda.textContent = r.rda != null && r.rda !== '' ? decode(String(r.rda)) : '-';
        row.appendChild(name);
        row.appendChild(amount);
        row.appendChild(rda);
        panel.appendChild(row);
      });
      container.appendChild(panel);

      if (parsed.note) {
        const note = document.createElement('div');
        note.className = 'sns-facts__note';
        note.textContent = decode(String(parsed.note));
        container.appendChild(note);
      }
      container.hidden = false;
      return true;
    }

    /* Renders parsed.other (the "other ingredients" — capsule/filler/carrier
       ingredients, not the name/amount/%RDA rows) as its own bordered box,
       separate from the facts table, matching the reference layout in
       sections/custom-product-modal-popup.liquid (.sfaoi-ingredients). Shown
       whenever the list is non-empty, independent of the main facts table. */
    function buildOtherIngredients(container, parsed) {
      if (!container) return false;
      container.textContent = '';
      container.hidden = true;
      if (!parsed || !parsed.other || !parsed.other.length) return false;

      const panel = document.createElement('div');
      panel.className = 'sns-facts sns-facts--other';

      const title = document.createElement('div');
      title.className = 'sns-facts__title';
      title.textContent = decode(String(parsed.otherTitle));
      panel.appendChild(title);

      const list = document.createElement('div');
      list.className = 'sns-facts__other-list';
      parsed.other.forEach((item) => {
        const row = document.createElement('div');
        row.className = 'sns-facts__other-row';
        row.textContent = item;
        list.appendChild(row);
      });
      panel.appendChild(list);

      container.appendChild(panel);
      container.hidden = false;
      return true;
    }

    /* Ingredients block, shared by BOTH quick-view shells (each queries its
       own scoped hooks — desktop has [data-sns-qv-facts] + [data-sns-qv-other]
       only; mobile also has [data-sns-qv-ingredients] as a last-resort text
       fallback for products with no structured facts data at all).
       Priority: facts table, then the separate "Other Ingredients" box, then
       (mobile only, and only once neither of those rendered) plain text —
       the product's own ingredients field, else the raw supplement-facts
       string. Desktop has no text hook, so once the table/other boxes are in
       place it never falls back to text. */
    function fillQvIngredients(shell, p) {
      if (!shell) return;
      const facts = qs(shell, '[data-sns-qv-facts]');
      const other = qs(shell, '[data-sns-qv-other]');
      const ing = qs(shell, '[data-sns-qv-ingredients]');
      if (!facts && !other && !ing) return;
      const label = (facts && labelOf(facts)) || (other && labelOf(other)) || (ing && labelOf(ing)) || null;
      const parsed = parseFacts(p.supplementFacts);
      const factsShown = buildFacts(facts, parsed);
      const otherShown = buildOtherIngredients(other, parsed);
      let text = null;
      if (ing && !factsShown) {
        if (p.ingredients) text = stripHtml(p.ingredients);
        else if (!otherShown && typeof p.supplementFacts === 'string' && p.supplementFacts.trim())
          text = decode(stripHtml(p.supplementFacts));
      }
      if (ing) {
        ing.hidden = !text;
        if (text) ing.textContent = text;
      }
      if (label) label.hidden = !(factsShown || otherShown || text);
    }

    function fillQvMobile(p, pr) {
      const shell = els.qvMobile;
      if (!shell) return;
      fillQvShared(shell, p, pr);
      fillQvIngredients(shell, p);

      /* Spec chips */
      setText(shell, '[data-sns-qv-chip-diet]', p.diet || '');
      setText(shell, '[data-sns-qv-chip-meantfor]', p.meantFor || '');
      setText(shell, '[data-sns-qv-chip-unit]', p.pack || '');
      const shelfWrap = qs(shell, '[data-sns-qv-chip-shelflife-wrap]');
      if (shelfWrap) {
        shelfWrap.hidden = !p.shelfLife;
        if (p.shelfLife) setText(shell, '[data-sns-qv-chip-shelflife]', p.shelfLife);
      }

      /* Carousel: one slide per product image (payload `images[]`, falling
         back to the single `image`, else one empty placeholder slide). Dots
         appear only when there is more than one image. URLs are never
         entity-decoded. */
      const imgs = Array.isArray(p.images) && p.images.length ? p.images : p.image ? [p.image] : [];
      const track = qs(shell, '[data-sns-qv-carousel-track]');
      const dots = qs(shell, '[data-sns-qv-dots]');
      if (track) {
        track.textContent = '';
        track.scrollLeft = 0;
        if (imgs.length) {
          imgs.forEach((src) => {
            const slide = document.createElement('div');
            slide.className = 'sns-qv-slide';
            const img = document.createElement('img');
            img.src = src;
            img.alt = p.title || '';
            img.loading = 'lazy';
            slide.appendChild(img);
            track.appendChild(slide);
          });
        } else {
          const slide = document.createElement('div');
          slide.className = 'sns-qv-slide';
          track.appendChild(slide);
        }
      }
      if (dots) {
        dots.textContent = '';
        dots.hidden = imgs.length <= 1;
        if (imgs.length > 1) {
          for (let i = 0; i < imgs.length; i++) {
            const d = document.createElement('span');
            d.className = 'sns-qv-dot' + (i === 0 ? ' is-active' : '');
            dots.appendChild(d);
          }
        }
      }
    }

    /* ------------- overlays: scroll lock, open/close, focus restore ------- */
    let lastFocus = null;
    let lockCount = 0;
    let savedOverflow = '';
    function lockScroll() {
      if (lockCount++ === 0) {
        savedOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      }
    }
    function unlockScroll() {
      if (lockCount > 0 && --lockCount === 0) document.body.style.overflow = savedOverflow;
    }
    function restoreFocus() {
      if (lastFocus && document.contains(lastFocus) && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      }
      lastFocus = null;
    }

    function openQuickView(id, opener) {
      const p = byId.get(String(id));
      if (!p) return;
      state.openProduct = String(id);
      const pr = pricesOf(p);
      fillQvDesktop(p, pr);
      fillQvMobile(p, pr);
      if (els.qvDesktop) els.qvDesktop.hidden = false;
      if (els.qvMobile) els.qvMobile.hidden = false;
      lastFocus = opener || document.activeElement;
      lockScroll();
    }
    function closeQuickView() {
      if (!state.openProduct) return;
      state.openProduct = null;
      if (els.qvDesktop) els.qvDesktop.hidden = true;
      if (els.qvMobile) els.qvMobile.hidden = true;
      unlockScroll();
      restoreFocus();
    }
    /* Prototype addFromDetail: qty becomes max(1, qty), then the view closes. */
    function addFromDetail() {
      const id = state.openProduct;
      if (!id) return;
      const p = byId.get(id);
      if (!p || !p.available) return;
      state.qty[id] = Math.max(1, qtyOf(id));
      persist();
      closeQuickView();
      render();
    }

    function openCartSheet(opener) {
      if (!els.cartSheet) return;
      updateBottombarSpacer(); // ensure --sns-bottombar-h is current before the sheet reads it
      state.openSheet = 'cart';
      els.cartSheet.hidden = false;
      lastFocus = opener || document.activeElement;
      lockScroll();
    }
    function closeCartSheet() {
      if (state.openSheet !== 'cart') return;
      state.openSheet = null;
      if (els.cartSheet) els.cartSheet.hidden = true;
      unlockScroll();
      restoreFocus();
    }

    /* ---------------- checkout ---------------- */
    let checkoutBusy = false;
    function clearCheckoutErrors() {
      qsa(root, '.sns-checkout-error').forEach((n) => n.remove());
    }
    function showCheckoutError(btn, msg) {
      const div = document.createElement('div');
      div.className = 'sns-checkout-error';
      div.setAttribute('role', 'alert');
      div.textContent = msg;
      btn.insertAdjacentElement('afterend', div);
    }
    /**
     * The Breeze web component normally forwards `subscriptionplanid` from
     * the clicked <breeze-button> to #breezeCheckout inside its own click
     * handler. This page intentionally bypasses that handler while it syncs
     * the selected products into Shopify's cart, so copy the plan into the
     * live SDK targets ourselves before calling startCheckout().
     *
     * Breeze also creates a hidden sample <breeze-button>; depending on load
     * order, that instance can own window.breeze.startCheckout. Keeping every
     * instance in sync prevents the hidden button's empty plan from winning.
     */
    function syncBreezeSubscriptionPlan(btn) {
      const planId = btn && btn.getAttribute('subscriptionplanid');
      if (!planId) return;

      qsa(document, 'breeze-button').forEach((breezeBtn) => {
        breezeBtn.setAttribute('subscriptionplanid', planId);
        if ('subscriptionplanid' in breezeBtn) breezeBtn.subscriptionplanid = planId;
      });

      const checkoutView = document.getElementById('breezeCheckout');
      if (checkoutView) {
        checkoutView.setAttribute('subscriptionplanid', planId);
        if ('subscriptionplanid' in checkoutView) checkoutView.subscriptionplanid = planId;
      }
    }
    /**
     * Opens Breeze's checkout overlay directly via window.breeze.startCheckout().
     * Called with no arguments, Breeze reads the live cart via /cart.js — so
     * this must only run once the real cart holds exactly the S&S selection
     * (see checkout() below), not before. Falls back to native checkout if
     * the Breeze SDK hasn't finished loading.
     */
    function openBreezeCheckout(btn) {
      syncBreezeSubscriptionPlan(btn);
      try {
        if (window.breeze && typeof window.breeze.startCheckout === 'function') {
          window.breeze.startCheckout();
          return;
        }
      } catch (e) {
        console.error('[sns] Breeze startCheckout failed:', e);
      }
      window.location.href = '/checkout';
    }
    function checkout(btn) {
      if (checkoutBusy) return;
      clearCheckoutErrors();
      const items = products
        .filter((p) => qtyOf(String(p.id)) > 0 && p.available && Number(p.variantId) > 0)
        .map((p) => ({ id: Number(p.variantId), quantity: qtyOf(String(p.id)) }));
      if (!items.length) return;
      checkoutBusy = true;
      btn.disabled = true;
      btn.classList.add('is-loading');
      /* Checkout must contain ONLY the products picked on this page, so the
         real Shopify cart is cleared first — any items added from other
         pages are dropped, not merged in. */
      fetch('/cart/clear.js', { method: 'POST' })
        .then((res) => {
          if (!res.ok) throw new Error('Could not prepare your cart. Please try again.');
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
          });
        })
        .then(async (res) => {
          if (res.ok) {
            openBreezeCheckout(btn);
            return;
          }
          let msg = 'Could not add items to the cart. Please try again.';
          try {
            const data = await res.json();
            msg = data.description || data.message || msg;
          } catch (e) {
            /* non-JSON error body */
          }
          throw new Error(msg);
        })
        .catch((err) => {
          console.error('[sns] Checkout failed:', err);
          showCheckoutError(btn, err && err.message ? err.message : 'Something went wrong. Please try again.');
        })
        .finally(() => {
          checkoutBusy = false;
          btn.disabled = false;
          btn.classList.remove('is-loading');
        });
    }

    /* ---------------- sticky offset & header clearance ---------------- */
    /* The Impact header is sticky AND hides/shows on scroll, so a one-time
       height measurement is wrong. Instead: pick the sticky header element
       once, then track its live viewport bottom on scroll/resize. */
    function findStickyIn(el) {
      const cs = getComputedStyle(el);
      if ((cs.position === 'sticky' || cs.position === 'fixed') && el.getBoundingClientRect().height > 0) return el;
      for (const child of el.children) {
        const cc = getComputedStyle(child);
        if ((cc.position === 'sticky' || cc.position === 'fixed') && child.getBoundingClientRect().height > 0)
          return child;
      }
      return null;
    }
    let headerEl = null;
    function pickHeader() {
      const sels = [
        'store-header',
        'header.header',
        '.header-wrapper',
        '#shopify-section-header',
        '[id^="shopify-section"][id*="header"]'
      ];
      for (const sel of sels) {
        let el = null;
        try {
          el = document.querySelector(sel);
        } catch (e) {
          continue;
        }
        if (!el) continue;
        const sticky = findStickyIn(el);
        if (sticky) return sticky;
      }
      return null;
    }
    /* --sns-sticky-top follows the header's live bottom edge (0 when the
       header has slid away or none was found), clamped to [0, 400]. */
    function updateStickyTop() {
      let top = 0;
      try {
        if (headerEl) top = Math.round(headerEl.getBoundingClientRect().bottom);
      } catch (e) {
        top = 0;
      }
      top = Math.min(400, Math.max(0, top));
      root.style.setProperty('--sns-sticky-top', top + 'px');
    }
    /* At page top the sticky header overlaps the section's hero — pad the
       root down past it. Measured from current rects + scrollY (no forced
       scroll); only trustworthy near scrollY 0, so it is skipped otherwise
       (mobile has no overlap — verified). */
    let basePadTop = null;
    function applyHeaderClearance() {
      try {
        if (!headerEl || window.scrollY > 2) return;
        if (basePadTop == null) {
          const prevInline = root.style.paddingTop;
          root.style.paddingTop = '';
          basePadTop = parseFloat(getComputedStyle(root).paddingTop) || 0;
          root.style.paddingTop = prevInline;
        }
        /* Padding sits inside the root, so our own inline padding never moves
           rect.top — this measurement stays stable across re-applications. */
        const rootTopDoc = root.getBoundingClientRect().top + window.scrollY;
        const headerBottomDoc = headerEl.getBoundingClientRect().bottom + window.scrollY;
        const overlap = Math.min(400, headerBottomDoc - rootTopDoc);
        root.style.paddingTop = overlap > 0 ? Math.round(basePadTop + overlap + 16) + 'px' : '';
      } catch (e) {
        /* leave CSS padding as-is */
      }
    }
    /* rAF-throttled scroll tracking */
    let stickyRafQueued = false;
    function onScrollSticky() {
      if (stickyRafQueued) return;
      stickyRafQueued = true;
      requestAnimationFrame(() => {
        stickyRafQueued = false;
        updateStickyTop();
      });
    }
    function measureSticky() {
      if (!headerEl) headerEl = pickHeader();
      updateStickyTop();
      applyHeaderClearance();
    }

    /* ---------------- master render ---------------- */
    function render() {
      renderRows();
      renderCart();
      if (state.openProduct) {
        const p = byId.get(state.openProduct);
        if (p) {
          fillQvControls(els.qvDesktop, p);
          fillQvControls(els.qvMobile, p);
        }
      }
    }
    /* Scroll the numbered steps banner into view below the sticky nav when a
       goal tab changes its content, but only if it isn't already fully
       visible (avoids a pointless jump when the user can already see it). */
    function scrollStepsIntoView() {
      const banner = qs(root, '[data-sns-steps]');
      if (!banner) return;
      const stickyTopPx = parseFloat(getComputedStyle(root).getPropertyValue('--sns-sticky-top')) || 0;
      const rect = banner.getBoundingClientRect();
      const fullyVisible = rect.top >= stickyTopPx && rect.bottom <= window.innerHeight;
      if (fullyVisible) return;
      window.scrollTo({ top: rect.top + window.scrollY - stickyTopPx - 16, behavior: 'smooth' });
    }
    /* Scroll the desktop "Your Subscription" panel into view — used when the
       header cart icon is clicked on this page (see the document-level
       listener below). Only relevant at the >=1300px layout where els.cart
       is actually rendered/visible (below that, the mobile cart sheet is
       used instead). */
    function scrollCartIntoView() {
      if (!els.cart) return;
      const stickyTopPx = parseFloat(getComputedStyle(root).getPropertyValue('--sns-sticky-top')) || 0;
      const rect = els.cart.getBoundingClientRect();
      const fullyVisible = rect.top >= stickyTopPx && rect.bottom <= window.innerHeight;
      if (fullyVisible) return;
      window.scrollTo({ top: rect.top + window.scrollY - stickyTopPx - 16, behavior: 'smooth' });
    }
    function selectFilter(goal) {
      if (!goal || goal === state.filter) return;
      state.filter = goal;
      renderTabsActive();
      renderSteps();
      renderRows();
      scrollStepsIntoView();
    }

    /* ---------------- events ---------------- */
    const itemIdOf = (el) => {
      const host = el.closest('[data-product-id]');
      return host ? host.dataset.productId : null;
    };

    /* Header cart icon (a[aria-controls="cart-drawer"], plus the theme's
       mobile bottom-nav cart icon, which just re-clicks that same anchor)
       should jump to *this page's* subscription cart instead of opening the
       theme's real Shopify cart drawer — that drawer reflects the real
       cart, which stays empty on this page until Secure Checkout runs, so
       showing it here would only confuse. This listener only ever exists on
       pages that render this section (subscribe-and-save.js is loaded
       exclusively from sections/subscribe-and-save.liquid), so it never
       touches cart-icon behavior anywhere else on the site. Capture phase
       is required so this runs before theme.js's own delegated listener on
       document.body (which calls preventDefault() unconditionally but
       cannot be stopped by preventDefault() alone — only stopPropagation()
       before it fires). >=1300px matches this section's own desktop layout
       breakpoint (where the "Your Subscription" panel is visible); below
       that, the mobile cart sheet is used instead. */
    document.addEventListener(
      'click',
      (e) => {
        const trigger = e.target.closest && e.target.closest('a[aria-controls="cart-drawer"]');
        if (!trigger) return;
        e.preventDefault();
        e.stopPropagation();
        if (window.matchMedia('(min-width: 1300px)').matches) {
          scrollCartIntoView();
        } else {
          openCartSheet(trigger);
        }
      },
      true
    );

    /* The visible "Secure Checkout" controls are the Breeze SDK's own
       <breeze-button> web component (markup untouched) — clicking one fires
       Breeze's internal checkout handler immediately, before this page's own
       cart sync ever runs, so Breeze opens against whatever (usually
       nothing) is already in the real Shopify cart. A capture-phase listener
       fires before that internal handler (which is attached down inside the
       component), so it can stop the click there, run the real add-to-cart
       sync, and only then open Breeze via openBreezeCheckout(). */
    root.addEventListener(
      'click',
      (e) => {
        const btn = e.target.closest && e.target.closest('#sns-breeze-checkout-desktop, #sns-breeze-checkout-mobile');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        checkout(btn);
      },
      true
    );

    root.addEventListener('click', (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      const hit = (sel) => t.closest(sel);

      const tab = hit('[data-sns-tab]');
      if (tab) {
        if (!tab.disabled) selectFilter(tab.getAttribute('data-goal'));
        return;
      }
      if (hit('[data-sns-search-clear]')) {
        state.query = '';
        if (els.search) {
          els.search.value = '';
          els.search.focus();
        }
        if (els.searchClear) els.searchClear.hidden = true;
        renderRows();
        return;
      }
      if (hit('[data-sns-tabs-prev]')) return scrollTabs(-1);
      if (hit('[data-sns-tabs-next]')) return scrollTabs(1);
      if (hit('[data-sns-addall]')) return addAll();

      /* Quick-view controls (distinct hooks, checked before generic ones) */
      if (hit('[data-sns-qv-inc]')) {
        if (state.openProduct) stepQty(state.openProduct, 1);
        return;
      }
      if (hit('[data-sns-qv-dec]')) {
        if (state.openProduct) stepQty(state.openProduct, -1);
        return;
      }
      if (hit('[data-sns-qv-add]')) return addFromDetail();

      /* Row / cart-item / sheet-item controls */
      const inc = hit('[data-sns-inc]');
      if (inc) {
        const id = itemIdOf(inc);
        if (id) stepQty(id, 1);
        return;
      }
      const dec = hit('[data-sns-dec]');
      if (dec) {
        const id = itemIdOf(dec);
        if (id) stepQty(id, -1);
        return;
      }
      const add = hit('[data-sns-add]');
      if (add) {
        const id = itemIdOf(add);
        if (id) stepQty(id, 1);
        return;
      }
      const rem = hit('[data-sns-remove]');
      if (rem) {
        const id = itemIdOf(rem);
        if (id) removeItem(id);
        return;
      }
      const opener = hit('[data-sns-row-open]');
      if (opener) {
        const id = itemIdOf(opener);
        if (id) openQuickView(id, opener);
        return;
      }

      /* Sheets & modal open/close */
      if (hit('[data-sns-bottombar-open]') || hit('[data-sns-thumbs]') || hit('[data-sns-proceed]')) {
        openCartSheet(t.closest('button'));
        return;
      }
      if (hit('[data-sns-sheet-close="cart"]') || hit('[data-sns-sheet-overlay="cart"]')) return closeCartSheet();
      if (hit('[data-sns-sheet-close="product"]') || hit('[data-sns-sheet-overlay="product"]'))
        return closeQuickView();
      if (hit('[data-sns-modal-close="quickview"]') || hit('[data-sns-modal-overlay="quickview"]'))
        return closeQuickView();

      const co = hit('[data-sns-checkout], [data-sns-checkout-mobile]');
      if (co) checkout(co);
    });

    /* Keyboard activation for role=button row openers */
    root.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const t = e.target;
      if (!t || !t.closest) return;
      const opener = t.closest('[data-sns-row-open]');
      if (!opener || opener.tagName === 'BUTTON') return;
      e.preventDefault();
      const id = itemIdOf(opener);
      if (id) openQuickView(id, opener);
    });

    /* Escape closes the topmost overlay */
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (state.openProduct) closeQuickView();
      else if (state.openSheet) closeCartSheet();
    });

    /* Search */
    if (els.search) {
      els.search.addEventListener('input', () => {
        state.query = els.search.value;
        if (els.searchClear) els.searchClear.hidden = state.query.length === 0;
        renderRows();
      });
    }

    /* Tabs scroller */
    if (els.track) els.track.addEventListener('scroll', updateTabScroll, { passive: true });

    /* Mobile product-sheet carousel dots follow scroll */
    (function wireCarousel() {
      const track = qs(els.qvMobile, '[data-sns-qv-carousel-track]');
      const dots = qs(els.qvMobile, '[data-sns-qv-dots]');
      if (!track || !dots) return;
      track.addEventListener(
        'scroll',
        () => {
          const kids = Array.from(dots.children);
          if (!kids.length) return;
          const raw = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          const i = Math.min(kids.length - 1, Math.max(0, raw));
          kids.forEach((d, idx) => d.classList.toggle('is-active', idx === i));
        },
        { passive: true }
      );
    })();

    /* Swipe-down on the product-sheet handle closes it (prototype behavior) */
    (function wireSheetSwipe() {
      const handle = qs(els.qvMobile, '[data-sns-qv-touch-handle]');
      if (!handle) return;
      let startY = null;
      handle.addEventListener(
        'touchstart',
        (e) => {
          startY = e.touches && e.touches[0] ? e.touches[0].clientY : null;
        },
        { passive: true }
      );
      handle.addEventListener(
        'touchend',
        (e) => {
          const endY = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : 0;
          if (startY != null && endY - startY > 55) closeQuickView();
          startY = null;
        },
        { passive: true }
      );
    })();

    /* Scroll: the theme header hides/shows — track its live bottom edge */
    window.addEventListener('scroll', onScrollSticky, { passive: true });

    /* Resize: sticky offset + header clearance + tab arrows */
    window.addEventListener(
      'resize',
      debounce(() => {
        basePadTop = null; // base padding differs per breakpoint — re-read after resize
        measureSticky();
        updateTabScroll();
        updateBottombarSpacer(); // safe-area insets can change on rotation
      }, 150)
    );

    /* ---------------- boot ---------------- */
    buildTabs();
    renderTabsActive();
    renderSteps();
    render();
    requestAnimationFrame(updateTabScroll);
    measureSticky();
    setTimeout(measureSticky, 1000); // late theme-header layout (fonts, announcement bar)
    updateBottombarSpacer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
